import { Link } from 'react-router-dom'
import type { HoneybookConnectionDto } from '@lib/dto/honeybook.dto'
import { HONEYBOOK_EVENT_PROBLEM_COPY } from '@lib/enums/honeybook-event-problem.enum'
import { instantLabel } from '@/lib/instantLabel'

function ListTitle({ title, hint }: { title: string; hint: string }) {
  return (
    <div>
      <h3 className="m-0 text-sm font-semibold text-ink">{title}</h3>
      <p className="m-0 text-sm text-ink-muted">{hint}</p>
    </div>
  )
}

const itemClass = 'border-b border-line py-2.5 text-sm last:border-b-0'
const linkClass = 'font-medium text-ink underline underline-offset-2'

/**
 * What the venue's HoneyBook settings list about its events (docs/honeybook.plan.md,
 * 4.5): booked projects waiting for a date, events HoneyBook put on a lane
 * another holds, and events that can't follow their project. Nothing when all
 * three are empty.
 */
export function HoneyBookEventLists({ connection }: { connection: HoneybookConnectionDto }) {
  const { waiting, clashes, problems } = connection
  if (waiting.length === 0 && clashes.length === 0 && problems.length === 0) return null
  return (
    <div className="space-y-6">
      {clashes.length === 0 ? null : (
        <section className="space-y-2">
          <ListTitle title="Two events on one lane" hint="HoneyBook booked these on a day and track another event already has. Move one of each pair." />
          <ul className="m-0 list-none p-0">
            {clashes.map((c) => (
              <li key={c.eventId} className={itemClass}>
                <Link to={`/events/${c.eventId}`} className={linkClass}>
                  {c.eventName}
                </Link>{' '}
                and{' '}
                <Link to={`/events/${c.withEventId}`} className={linkClass}>
                  {c.withEventName}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}
      {problems.length === 0 ? null : (
        <section className="space-y-2">
          <ListTitle title="Events not following HoneyBook" hint="Each stays as it was until what’s wrong is fixed in HoneyBook." />
          <ul className="m-0 list-none p-0">
            {problems.map((p) => (
              <li key={p.eventId} className={itemClass}>
                <Link to={`/events/${p.eventId}`} className={linkClass}>
                  {p.eventName}
                </Link>
                <span className="text-ink-muted"> · {HONEYBOOK_EVENT_PROBLEM_COPY[p.problem]}</span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {waiting.length === 0 ? null : (
        <section className="space-y-2">
          <ListTitle title="Bookings waiting for a date" hint="Booked in HoneyBook with no date yet. Each becomes an event here once HoneyBook has its date." />
          <ul className="m-0 list-none p-0">
            {waiting.map((w) => (
              <li key={w.projectId} className={`${itemClass} text-ink-muted`}>
                A couple who inquired {instantLabel(w.inquiredAt)}
                {w.openUrl === null ? null : (
                  <>
                    {' · '}
                    <a href={w.openUrl} target="_blank" rel="noreferrer" className={linkClass}>
                      Open in HoneyBook
                    </a>
                  </>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
